@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@500;600;700&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&family=Manrope:wght@400;500;600;700;800&family=STIX+Two+Math&family=STIX+Two+Text:wght@400;500;600;700&display=swap');

@tailwind base;
@tailwind components;
@tailwind utilities;

@layer base {
  :root {
    --ink-navy: #333336;
    --ink-blue: #454549;
    --ink-rose: #59595e;
    --ink-teal: #505055;
    --ink-plum: #646469;
    --paper: #f5f5f4;
    --paper-shadow: #dededc;
    --highlighter-yellow: rgba(130, 130, 130, .22);
    --highlighter-mint: rgba(110, 110, 110, .18);
    --background: 0 0% 96%;
    --foreground: 0 0% 12%;
    --primary: 0 0% 16%;
    --primary-foreground: 0 0% 96%;
    --border: 0 0% 85%;
    --font-display: 'Manrope', sans-serif;
    --font-body: 'DM Sans', sans-serif;
  }

  .dark {
    --background: 0 0% 7%;
    --foreground: 0 0% 95%;
    --primary: 0 0% 93%;
    --primary-foreground: 0 0% 10%;
    --border: 0 0% 21%;
  }

  html {
    font-family: var(--font-body);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }

  body {
    background-color: #0D0D0E;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    transition: background-color 0.5s ease;
  }

  /* Light mode body background */
  body.light {
    background-color: #f5f5f5;
  }

  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: #2A2A2E;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #3A3A3E;
  }
}

@layer components {
  .lesson-paper {
    background-color: var(--paper);
    background-image:
      linear-gradient(90deg, transparent 0, transparent 88px, rgba(209, 94, 117, .42) 88px, rgba(209, 94, 117, .42) 90px, transparent 90px),
      repeating-linear-gradient(to bottom, transparent 0, transparent 40px, rgba(70, 125, 181, .18) 41px, transparent 42px);
  }

  .lesson-note-type { font-family: 'Caveat', cursive; }
  .lesson-handwriting {
    font-family: 'Caveat', 'Segoe Print', 'Bradley Hand', cursive;
    letter-spacing: .005em;
  }
  .lesson-body-type { font-family: var(--font-body); }
  .lesson-label-type { font-family: 'DM Mono', monospace; }
  .lesson-math-equation {
    font-family: 'STIX Two Math', 'Cambria Math', 'STIX Two Text', Georgia, serif;
    font-variant-numeric: lining-nums tabular-nums;
  }

  .math-whiteboard-page {
    background-color: #ffffff;
  }
  .math-whiteboard-dark {
    background-color: #080b10;
  }
  .math-whiteboard-page .lesson-body-type {
    font-family: 'STIX Two Text', Georgia, serif;
  }
  .math-whiteboard-dark .lesson-diagram-panel {
    background: rgba(15, 23, 42, .82);
    border-color: rgba(103, 232, 249, .28);
  }
  .math-whiteboard-dark .lesson-diagram-panel svg text {
    fill: #e5e7eb;
    stroke: rgba(8, 11, 16, .92);
  }
  .math-whiteboard-dark .lesson-focus-marker::before {
    background: rgba(250, 204, 21, .38);
  }
  .math-whiteboard-dark .lesson-focus-underline {
    text-decoration-color: #67e8f9;
  }
  .math-whiteboard-dark .lesson-focus-circle {
    border-color: #f87171;
  }
  .lesson-focus-marker,
  .lesson-focus-underline,
  .lesson-focus-circle {
    display: inline-block;
    position: relative;
    z-index: 0;
    animation: lesson-focus-in .48s cubic-bezier(.22, 1, .36, 1) both;
  }
  .lesson-focus-marker::before {
    content: '';
    position: absolute;
    z-index: -1;
    inset: 48% -0.12em 0.02em;
    border-radius: .14em;
    background: rgba(250, 204, 21, .48);
    transform: rotate(-1.2deg);
    transform-origin: left center;
    animation: lesson-marker-write .65s cubic-bezier(.22, 1, .36, 1) both;
  }
  .lesson-focus-underline {
    text-decoration: underline;
    text-decoration-color: #2563eb;
    text-decoration-thickness: .1em;
    text-underline-offset: .16em;
  }
  .lesson-focus-circle {
    margin-inline: .08em;
    padding-inline: .16em;
    border: .08em solid rgba(220, 38, 38, .82);
    border-radius: 999px;
  }
  @keyframes lesson-focus-in {
    from { transform: scale(.94); }
    to { transform: scale(1); }
  }
  @keyframes lesson-marker-write {
    from { clip-path: inset(0 100% 0 0); }
    to { clip-path: inset(0 0 0 0); }
  }
  .lesson-diagram-panel svg text {
    font-family: 'DM Mono', monospace;
    paint-order: stroke fill;
    stroke: rgba(255, 254, 250, .88);
    stroke-width: 2px;
    stroke-linejoin: round;
  }

  .lesson-diagram-active svg path[fill="none"] {
    stroke-dasharray: 1300;
    stroke-dashoffset: 1300;
    animation: lesson-draw-line 1.15s cubic-bezier(.22, 1, .36, 1) forwards;
  }

  @keyframes lesson-draw-line {
    to { stroke-dashoffset: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .lesson-diagram-active svg path[fill="none"] { animation: none; stroke-dashoffset: 0; }
  }
}

@layer utilities {
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }

  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
}